import React, { MouseEvent, useEffect } from 'react';
import { Select } from 'antd';
import { CloseCircleFilled } from '@ant-design/icons';
import styled from 'styled-components';
import { Domain, EntityType } from '../../../../types.generated';
import { useEntityRegistry } from '../../../useEntityRegistry';
import ClickOutside from '../../../shared/ClickOutside';
import { BrowserWrapper } from '../../../shared/tags/AddTagsTermsModal';
import useParentSelector from './useParentSelector';
import DomainNavigator from '../../../domain/nestedDomains/domainNavigator/DomainNavigator';
import { useDomainsContext } from '../../../domain/DomainsContext';
import ParentEntities from '../../../search/filters/ParentEntities';
import { getParentDomains } from '../../../domain/utils';
import { useTranslation } from '../../../../i18n/useTranslation';

const SearchResultContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
`;

// filter out entity itself and its children
export function filterResultsForMove(entity: Domain, entityUrn: string) {
    return (
        entity.urn !== entityUrn &&
        entity.__typename === 'Domain' &&
        !entity.parentDomains?.domains.some((node) => node.urn === entityUrn)
    );
}

interface Props {
    selectedParentUrn: string;
    setSelectedParentUrn: (parent: string) => void;
    isMoving?: boolean;
}

export default function DomainParentSelect({ selectedParentUrn, setSelectedParentUrn, isMoving }: Props) {
    const entityRegistry = useEntityRegistry();
    const { entityData } = useDomainsContext();
    const domainUrn = entityData?.urn;

    const {
        searchResults,
        searchQuery,
        isFocusedOnInput,
        selectedParentName,
        selectParentFromBrowser,
        onSelectParent,
        handleSearch,
        clearSelectedParent,
        setIsFocusedOnInput,
    } = useParentSelector({
        entityType: EntityType.Domain,
        entityData,
        selectedParentUrn,
        setSelectedParentUrn,
    });
    const domainSearchResultsFiltered =
        isMoving && domainUrn
            ? searchResults.filter((r) => filterResultsForMove(r.entity as Domain, domainUrn))
            : searchResults;

    function selectDomain(domain: Domain) {
        selectParentFromBrowser(domain.urn, entityRegistry.getDisplayName(EntityType.Domain, domain));
    }

    const isShowingDomainNavigator = !searchQuery && isFocusedOnInput;

    const handleFocus = () => setIsFocusedOnInput(true);
    const handleClickOutside = () => setIsFocusedOnInput(false);

    const handleClear = (event: MouseEvent) => {
        // Prevent, otherwise antd will close the select menu but leaves it focused
        event.stopPropagation();
        clearSelectedParent();
    };

    // Handle ESC key to close dropdown without closing modal
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && isShowingDomainNavigator) {
                event.stopPropagation();
                event.preventDefault();
                setIsFocusedOnInput(false);
            }
        };

        document.addEventListener('keydown', handleKeyDown, { capture: true });
        return () => {
            document.removeEventListener('keydown', handleKeyDown, { capture: true });
        };
    }, [isShowingDomainNavigator, setIsFocusedOnInput]);

    const { t } = useTranslation();

    return (
        <ClickOutside onClickOutside={handleClickOutside}>
            <Select
                allowClear
                showSearch
                filterOption={false}
                onFocus={handleFocus}
                onSearch={handleSearch}
                onSelect={onSelectParent}
                placeholder={t('Select')}
                value={selectedParentName}
                aria-label={t('Select Parent')}
                clearIcon={<CloseCircleFilled onClick={handleClear} />}
                dropdownStyle={isShowingDomainNavigator || !searchQuery ? { display: 'none' } : {}}
            >
                {domainSearchResultsFiltered.map((result) => (
                    <Select.Option key={result?.entity?.urn} value={result.entity.urn}>
                        <SearchResultContainer>
                            <ParentEntities parentEntities={getParentDomains(result.entity, entityRegistry)} />
                            {entityRegistry.getDisplayName(result.entity.type, result.entity)}
                        </SearchResultContainer>
                    </Select.Option>
                ))}
            </Select>
            <BrowserWrapper isHidden={!isShowingDomainNavigator}>
                <DomainNavigator
                    domainUrnToHide={isMoving ? domainUrn : undefined}
                    selectDomainOverride={selectDomain}
                    isDomainAccessible={isFocusedOnInput}
                />
            </BrowserWrapper>
        </ClickOutside>
    );
}
